import React, { useState, useEffect } from "react";
import axios, { baseURL } from "utils/api";
import {
  Grid,
  Avatar,
  Chip,
  Card,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
} from "@mui/material";
import ArgonBox from "components/ArgonBox";
import {
  Person,
  Phone,
  Home,
  CalendarToday,
  School,
  PersonOutline,
  EmojiEvents,
  AttachMoney,
  Schedule,
  Payment,
  VideoCall,
  Edit,
  ContentCopy,
} from "@mui/icons-material";
import ArgonTypography from "components/ArgonTypography";
import Table from "examples/Tables/Table";
import PersonIcon from "@mui/icons-material/Person";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SummaryTableData from "Views/Activities/ClassMgt/data/summaryTableData";
import ArgonButton from "components/ArgonButton";

import MiniDialog from "components/MiniDialog/MiniDialog";
import { toast } from "react-toastify";
import ArgonInput from "components/ArgonInput";

function summary({ classData }) {
  // Default values if classData is not available
  const parent = classData?.parentId || {};
  // console.log("Parent Data in Summary:", parent);
  const student = classData?.student || {};
  const tutor = classData?.tutor || {};
  const studentsList = classData?.students || [];
  const { columns, rows } = SummaryTableData(studentsList);
  const [open, setOpen] = useState(false);

  const handleClick = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const [open1, setOpen1] = useState(false);
  const handleClick1 = () => setOpen1(true);
  const handleClose1 = () => setOpen1(false);

  const [openMeetingDialog, setOpenMeetingDialog] = useState(false);
  const [meetingLinkInput, setMeetingLinkInput] = useState("");
  const [meetingLink, setMeetingLink] = useState(classData?.meetingDetails?.meetingLink || "");

  useEffect(() => {
    setMeetingLink(classData?.meetingDetails?.meetingLink || "");
  }, [classData?.meetingDetails?.meetingLink]);

  const handleOpenMeetingDialog = () => {
    setMeetingLinkInput(meetingLink || classData?.meetingDetails?.meetingLink || "");
    setOpenMeetingDialog(true);
  };

  const handleCloseMeetingDialog = () => {
    setOpenMeetingDialog(false);
    setMeetingLinkInput("");
  };

  const handleCopyMeetingLink = () => {
    const link = meetingLink;
    if (link) {
      navigator.clipboard.writeText(link);
      toast.success("Meeting link copied!");
    } else {
      toast.error("No meeting link available");
    }
  };

  const handleUpdateMeetingLink = async () => {
    // Add your API call here to update meeting link
    try {
      if (!classData?._id) throw new Error("Missing class id");

      const payload = { meetingDetails: { meetingLink: meetingLinkInput } };
      const res = await axios.put(`/classes/session/${classData._id}`, payload);
      if (res?.data?.success) {
        // Persisted on backend — update local UI
        setMeetingLink(meetingLinkInput);
        toast.success(res.data?.message || "Meeting link updated successfully!");
        handleCloseMeetingDialog();
      } else {
        throw new Error(res?.data?.message || "Failed to update meeting link");
      }
    } catch (error) {
      toast.error(error.response?.data?.error || error.message || "Failed to update meeting link");
      console.error("Meeting link update error:", error);
    }
  };

  // Calculate dynamic pricing and session information
  // IMPORTANT: Use totalNoOfClasses first (total for all cycles), then fallback to first cycle
  const totalClasses = classData?.totalNoOfClasses || classData?.cycles?.[0]?.totalNoOfClasses || 0;
  const totalMonths = classData?.noOfMonths || 1;
  const paymentAmount = classData?.paymentAmount || 0;
  const monthlyClasses = totalClasses / totalMonths;
  const weeklyClasses = monthlyClasses / 4;

  // console.log("📊 Classes Calculation:");
  // console.log("Total Classes (all cycles):", classData?.totalNoOfClasses);
  // console.log("First Cycle Classes:", classData?.cycles?.[0]?.totalNoOfClasses);
  // console.log("Using Total Classes:", totalClasses);

  // Duration can be in minutes or hours - need to normalize to hours
  const durationValue = classData?.duration || 60; // default 60 minutes
  const sessionDurationInHours = durationValue > 24 ? durationValue / 60 : durationValue; // if > 24, it's in minutes, convert to hours

  const weeklyHours = weeklyClasses * sessionDurationInHours;
  const monthlyHours = weeklyHours * 4;
  const totalHours = totalClasses * sessionDurationInHours;

  // console.log("⏱️ Duration Value:", durationValue);
  // console.log("⏱️ Session Duration (hours):", sessionDurationInHours);

  // Use stored values from backend (calculated and stored at activation time)
  // These values are already calculated when class is activated in activateClassSession API
  const feeCharge = classData?.paymentAmount / totalClasses || 0;
  const tutorSalary = classData?.tutorSalary || 0;

  const weeklySessionCount = classData?.weeklySessionCount || Math.round(weeklyClasses);
  const monthlySessionCount = classData?.monthlySessionCount || Math.round(monthlyClasses);

  // Monthly Fee = Fee Charge per class × Sessions in a month
  const monthlyFee = feeCharge * monthlySessionCount;
  // Payscale = Tutor Salary per class × Sessions in a month
  const monthlyTutorSalary = tutorSalary * monthlySessionCount;

  console.log("💰 Using Stored Values from Backend:");
  console.log("Fee Charge per class (stored):", feeCharge);
  console.log("Tutor Salary per class (stored):", tutorSalary);
  console.log("Monthly Sessions:", monthlySessionCount);
  console.log("Monthly Fee (calculated):", monthlyFee);
  console.log("Monthly Tutor Salary (calculated):", monthlyTutorSalary);
  console.log("Payment Amount:", classData?.paymentAmount);
  console.log("Total Classes:", totalClasses);

  return (
    <ArgonBox>
      <Card
        sx={{
          borderRadius: "20px",
          overflow: "hidden",
          boxShadow: "0 6px 4px rgba(0,0,0,0.12)",
          mb: 3,
          background: "white",
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Card
              sx={{
                borderRadius: "16px",
                p: 2.5,
                boxShadow: "0 4px 15px rgba(0,0,0,0.06)",
                background: "linear-gradient(135deg, #ffffff 0%, #fbfbfb 100%)",
              }}
            >
              {/* --- Parent Info Section --- */}
              <Grid container spacing={2} alignItems="center" mb={2}>
                {/* Avatar + Name + Status */}
                <Grid item xs={12} sm={4} textAlign={{ xs: "center", sm: "left" }}>
                  <Avatar
                    src={parent.profileImage}
                    alt="Parent"
                    sx={{
                      width: 90,
                      height: 90,
                      border: "3px solid white",
                      boxShadow: "0 3px 10px rgba(0,0,0,0.15)",
                      mb: 1,
                    }}
                  />
                  <ArgonTypography variant="subtitle2" fontWeight="medium">
                    {parent.fullName || "N/A"}
                  </ArgonTypography>
                </Grid>

                {/* Parent Info Cards */}
                <Grid item xs={12} sm={8}>
                  <Grid container spacing={1.5}>
                    {/* Username */}
                    <Grid item xs={6} sm={4}>
                      <ArgonBox
                        sx={{
                          p: 1.5,
                          bgcolor: "#f9f9ff",
                          borderRadius: 2,
                          textAlign: "center",
                          "&:hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.08)" },
                        }}
                      >
                        <PersonIcon color="primary" sx={{ fontSize: 20, mb: 0.3 }} />
                        <ArgonTypography variant="caption" display="block" color="text">
                          Username
                        </ArgonTypography>
                        <ArgonTypography variant="body2" fontWeight="medium">
                          {parent.fullName || "N/A"}
                        </ArgonTypography>
                      </ArgonBox>
                    </Grid>

                    {/* Joining Date */}
                    <Grid item xs={6} sm={4}>
                      <ArgonBox
                        sx={{
                          p: 1.5,
                          bgcolor: "#f9f9ff",
                          borderRadius: 2,
                          textAlign: "center",
                          "&:hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.08)" },
                        }}
                      >
                        <CalendarTodayIcon color="primary" sx={{ fontSize: 20, mb: 0.3 }} />
                        <ArgonTypography variant="caption" display="block" color="text">
                          Joined
                        </ArgonTypography>
                        <ArgonTypography variant="body2" fontWeight="medium">
                          {parent.createdAt
                            ? new Date(parent.createdAt).toLocaleDateString()
                            : "N/A"}
                        </ArgonTypography>
                      </ArgonBox>
                    </Grid>

                    {/* Badge / Level */}
                    <Grid item xs={12} sm={4}>
                      <ArgonBox
                        sx={{
                          p: 1.5,
                          bgcolor: "#f9f9ff",
                          borderRadius: 2,
                          textAlign: "center",
                          "&:hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.08)" },
                        }}
                      >
                        <ArgonBox sx={{ fontSize: 20, mb: 0.3, color: "#fbc02d" }}>🎓</ArgonBox>
                        <ArgonTypography variant="caption" display="block" color="text">
                          Tutor Level
                        </ArgonTypography>
                        <ArgonTypography variant="body2" fontWeight="medium">
                          {tutor.tutorLevel || "Level 1"}
                        </ArgonTypography>
                      </ArgonBox>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>

              {/* Contact Information */}
              <Grid container spacing={2}>
                {/* Email */}
                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      minHeight: 120,
                      background: "linear-gradient(135deg, #e3f2fd, #bbdefb)",
                      border: "1px solid #90caf9",
                    }}
                  >
                    <ArgonBox display="flex" alignItems="center" gap={1} mb={1}>
                      <EmailIcon sx={{ color: "#1976d2", fontSize: 20 }} />
                      <ArgonTypography variant="subtitle2" fontWeight="medium">
                        Email ID
                      </ArgonTypography>
                    </ArgonBox>
                    <ArgonTypography variant="body2" fontWeight="medium">
                      {parent.email || "N/A"}
                    </ArgonTypography>
                  </Card>
                </Grid>

                {/* Tutor */}
                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      minHeight: 120,
                      background: "linear-gradient(135deg, #e8f5e8, #c8e6c9)",
                      border: "1px solid #81c784",
                    }}
                  >
                    <ArgonBox display="flex" alignItems="center" gap={1} mb={1}>
                      <PersonIcon sx={{ color: "#388e3c", fontSize: 20 }} />
                      <ArgonTypography variant="subtitle2" fontWeight="medium">
                        Tutor
                      </ArgonTypography>
                    </ArgonBox>
                    <ArgonTypography variant="body2" fontWeight="medium">
                      {tutor.fullName || "N/A"}
                    </ArgonTypography>
                  </Card>
                </Grid>

                {/* Phone Number */}
                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      minHeight: 120,
                      background: "linear-gradient(135deg, #fff3e0, #ffe0b2)",
                      border: "1px solid #ffb74d",
                    }}
                  >
                    <ArgonBox display="flex" alignItems="center" gap={1} mb={1}>
                      <PhoneIcon sx={{ color: "#f57c00", fontSize: 20 }} />
                      <ArgonTypography variant="subtitle2" fontWeight="medium">
                        Phone
                      </ArgonTypography>
                    </ArgonBox>
                    <ArgonTypography variant="body2" fontWeight="medium">
                      {parent.phone || "N/A"}
                    </ArgonTypography>
                  </Card>
                </Grid>

                {/* Address */}
                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      minHeight: 120,
                      background: "linear-gradient(135deg, #e0feffff, #b2e5ffff)",
                      border: "1px solid #4dffedff",
                    }}
                  >
                    <ArgonBox display="flex" alignItems="center" gap={1} mb={1}>
                      <LocationOnIcon sx={{ color: "#00acc1", fontSize: 20 }} />
                      <ArgonTypography variant="subtitle2" fontWeight="medium">
                        Address
                      </ArgonTypography>
                    </ArgonBox>
                    <ArgonTypography variant="caption" color="text" fontWeight="medium">
                      {`${student?.parentId?.address || ""} ${
                        student?.parentId?.city || ""
                      }`.trim() || "N/A"}
                    </ArgonTypography>
                  </Card>
                </Grid>

                {/* Mode */}
                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      minHeight: 120,
                      background: "linear-gradient(135deg, #fce4ec, #f8bbd0)",
                      border: "1px solid #f06292",
                    }}
                  >
                    <ArgonBox display="flex" alignItems="center" gap={1} mb={1}>
                      <School sx={{ color: "#c2185b", fontSize: 20 }} />
                      <ArgonTypography variant="subtitle2" fontWeight="medium">
                        Mode
                      </ArgonTypography>
                    </ArgonBox>
                    <ArgonTypography variant="body2" fontWeight="medium">
                      {classData?.mode
                        ? classData.mode.charAt(0).toUpperCase() +
                          classData.mode.slice(1).toLowerCase()
                        : "N/A"}
                    </ArgonTypography>
                  </Card>
                </Grid>

                {/* Meeting Link */}
                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      minHeight: 120,
                      background: "linear-gradient(135deg, #f3e5f5, #e1bee7)",
                      border: "1px solid #ba68c8",
                    }}
                  >
                    <ArgonBox
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      mb={1}
                    >
                      <ArgonBox display="flex" alignItems="center" gap={1}>
                        <VideoCall sx={{ color: "#7b1fa2", fontSize: 20 }} />
                        <ArgonTypography variant="subtitle2" fontWeight="medium">
                          Meeting Link
                        </ArgonTypography>
                      </ArgonBox>
                      <IconButton
                        size="small"
                        onClick={handleOpenMeetingDialog}
                        sx={{ color: "#7b1fa2" }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </ArgonBox>
                    {meetingLink ? (
                      <ArgonBox display="flex" alignItems="center" gap={1}>
                        <ArgonTypography
                          variant="caption"
                          color="text"
                          fontWeight="medium"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            flex: 1,
                          }}
                        >
                          {meetingLink}
                        </ArgonTypography>
                        <IconButton
                          size="small"
                          onClick={handleCopyMeetingLink}
                          sx={{ color: "#7b1fa2" }}
                        >
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </ArgonBox>
                    ) : (
                      <ArgonTypography variant="caption" color="text" fontWeight="medium">
                        No meeting link available
                      </ArgonTypography>
                    )}
                  </Card>
                </Grid>
              </Grid>
            </Card>
          </Grid>
        </Grid>
      </Card>

      <Card
        sx={{
          mt: 3,
          borderRadius: "16px",
          backgroundColor: "white",
          boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
          border: "1px solid #f0f0f0",
        }}
      >
        <ArgonBox p={3}>
          {/* Header */}
          <ArgonBox display="flex" alignItems="center" gap={2} mb={3}>
            <ArgonBox
              sx={{
                backgroundColor: "white",
                borderRadius: "12px",
                p: 1.2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 3px 8px rgba(0,0,0,0.08)",
              }}
            >
              <Payment sx={{ color: "#344767", fontSize: "22px" }} />
            </ArgonBox>
            <ArgonBox>
              <ArgonTypography variant="h5" fontWeight="bold" color="dark">
                Package Details {classData?.currentCycle ? `- ${classData.currentCycle}` : ""}
              </ArgonTypography>
              <ArgonTypography variant="body2" color="text">
                Pricing and session information
                {classData?.currentCycle ? ` for ${classData.currentCycle} months` : ""}
              </ArgonTypography>
            </ArgonBox>
          </ArgonBox>

          <Grid container spacing={3}>
            {/* Fee & Salary */}
            <Grid item xs={12} md={4}>
              <ArgonBox
                sx={{
                  backgroundColor: "white",
                  borderRadius: "12px",
                  p: 2.5,
                  boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                  border: "1px solid #f5f5f5",
                  height: "100%",
                }}
              >
                <ArgonBox display="flex" alignItems="center" gap={1.5} mb={2}>
                  <AttachMoney sx={{ color: "#4caf50", fontSize: "20px" }} />
                  <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                    Class Rates
                  </ArgonTypography>
                </ArgonBox>

                <ArgonBox mb={2}>
                  <ArgonBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={1}
                  >
                    <ArgonTypography variant="body2" color="text" fontWeight="medium">
                      Fee Charge per class
                    </ArgonTypography>
                    <ArgonTypography variant="body2" fontWeight="medium" color="dark">
                      ₹ {Math.round(feeCharge)}
                    </ArgonTypography>
                  </ArgonBox>
                  <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
                    <ArgonTypography variant="body2" color="text" fontWeight="medium">
                      Tutor Salary per class
                    </ArgonTypography>
                    <ArgonTypography variant="body2" fontWeight="medium" color="dark">
                      ₹ {Math.round(tutorSalary)}
                    </ArgonTypography>
                  </ArgonBox>
                </ArgonBox>
              </ArgonBox>
            </Grid>

            {/* Sessions */}
            <Grid item xs={12} md={4}>
              <ArgonBox
                sx={{
                  backgroundColor: "white",
                  borderRadius: "12px",
                  p: 2.5,
                  boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                  border: "1px solid #f5f5f5",
                  height: "100%",
                }}
              >
                <ArgonBox display="flex" alignItems="center" gap={1.5} mb={2}>
                  <Schedule sx={{ color: "#2196f3", fontSize: "20px" }} />
                  <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                    Sessions
                  </ArgonTypography>
                </ArgonBox>

                <ArgonBox mb={2}>
                  <ArgonBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={1}
                  >
                    <ArgonTypography variant="body2" color="text" fontWeight="medium">
                      Sessions in a week
                    </ArgonTypography>
                    <ArgonTypography variant="caption" fontWeight="medium" color="dark">
                      {weeklySessionCount} sessions | {weeklyHours.toFixed(1)} hours
                    </ArgonTypography>
                  </ArgonBox>
                  <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
                    <ArgonTypography variant="body2" color="text" fontWeight="medium">
                      Sessions in a month
                    </ArgonTypography>
                    <ArgonTypography variant="caption" fontWeight="medium" color="dark">
                      {monthlySessionCount} sessions / {monthlyHours.toFixed(1)} hours
                    </ArgonTypography>
                  </ArgonBox>
                </ArgonBox>
              </ArgonBox>
            </Grid>

            {/* Monthly Summary */}
            <Grid item xs={12} md={4}>
              <ArgonBox
                sx={{
                  backgroundColor: "white",
                  borderRadius: "12px",
                  p: 2.5,
                  boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                  border: "1px solid #f5f5f5",
                  height: "100%",
                }}
              >
                <ArgonBox display="flex" alignItems="center" gap={1.5} mb={2}>
                  <Payment sx={{ color: "#344767", fontSize: "20px" }} />
                  <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                    Monthly Summary
                  </ArgonTypography>
                </ArgonBox>

                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <ArgonBox textAlign="center">
                      <ArgonTypography variant="caption" color="text">
                        Monthly Fee
                      </ArgonTypography>
                      <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                        ₹{Math.round(monthlyFee)}
                      </ArgonTypography>
                    </ArgonBox>
                  </Grid>
                  <Grid item xs={4}>
                    <ArgonBox textAlign="center">
                      <ArgonTypography variant="caption" color="text">
                        Payscale
                      </ArgonTypography>
                      <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                        ₹{Math.round(monthlyTutorSalary)}
                      </ArgonTypography>
                    </ArgonBox>
                  </Grid>
                  <Grid item xs={4}>
                    <ArgonBox textAlign="center">
                      <ArgonTypography variant="caption" color="text">
                        Profit
                      </ArgonTypography>
                      <ArgonTypography variant="h6" fontWeight="bold" color="success">
                        ₹{Math.round(monthlyFee - monthlyTutorSalary)}
                      </ArgonTypography>
                    </ArgonBox>
                  </Grid>
                </Grid>
              </ArgonBox>
            </Grid>
          </Grid>
        </ArgonBox>
      </Card>

      <ArgonBox py={3}>
        <ArgonTypography variant="body1" fontWeight="medium" sx={{ px: 3 }} mb={2}>
          Student List
        </ArgonTypography>
        <ArgonBox
          sx={{
            "& .MuiTableRow-root:not(:last-child)": {
              "& td": {
                borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                  `${borderWidth[1]} solid ${borderColor}`,
              },
            },
          }}
        >
          <Table columns={columns} rows={rows} />
        </ArgonBox>
      </ArgonBox>

      <ArgonBox display="flex" gap={3} mt={3}>
        {/* Core Requirements Card */}
        <ArgonBox
          sx={{
            width: "50%",
            bgcolor: "background.paper",
            borderRadius: "16px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
            transition: "all 0.3s ease",
            "&:hover": { boxShadow: "0 8px 20px rgba(0,0,0,0.18)" },
          }}
          p={3}
        >
          {/* Header Row */}
          <ArgonBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <ArgonBox display="flex" alignItems="center" gap={1}>
              <ArgonTypography variant="h6" fontWeight="bold" color="primary">
                Core Requirements
              </ArgonTypography>
            </ArgonBox>

            <ArgonButton onClick={handleClick} variant="contained" size="small" color="primary">
              Add
            </ArgonButton>
          </ArgonBox>
        </ArgonBox>

        {/* Notes Card */}
        <ArgonBox
          sx={{
            width: "50%",
            bgcolor: "background.paper",
            borderRadius: "16px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
            transition: "all 0.3s ease",
            "&:hover": { boxShadow: "0 8px 20px rgba(0,0,0,0.18)" },
          }}
          p={3}
        >
          {/* Header Row */}
          <ArgonBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <ArgonBox display="flex" alignItems="center" gap={1}>
              <span style={{ fontSize: 28, marginRight: 6 }}>📝</span>
              <ArgonTypography variant="h6" fontWeight="bold" color="primary">
                Notes
              </ArgonTypography>
            </ArgonBox>

            <ArgonButton onClick={handleClick1} variant="contained" size="medium" color="primary">
              Add Note
            </ArgonButton>
          </ArgonBox>

          {/* Footer link */}
          <ArgonTypography
            variant="body2"
            color="secondary"
            sx={{
              cursor: "pointer",
              fontWeight: "bold",
              letterSpacing: 0.5,
              transition: "color 0.2s",
              "&:hover": { color: "#2379f1ff" },
            }}
          >
            Show All Notes →
          </ArgonTypography>
        </ArgonBox>
      </ArgonBox>

      <MiniDialog
        open={open}
        onClose={handleClose}
        title="Add Core requirement"
        label="Note"
        placeholder="Enter core requirement"
      />
      <MiniDialog
        open={open1}
        onClose={handleClose1}
        title="Add  Notes"
        label="Note"
        placeholder="Enter text"
      />

      {/* Meeting Link Update Dialog */}
      <Dialog open={openMeetingDialog} onClose={handleCloseMeetingDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <ArgonBox display="flex" alignItems="center" gap={1}>
            <VideoCall sx={{ color: "#7b1fa2" }} />
            <ArgonTypography variant="h6" fontWeight="bold">
              Update Meeting Link
            </ArgonTypography>
          </ArgonBox>
        </DialogTitle>
        <DialogContent>
          <ArgonBox pt={1}>
            <ArgonInput
              fullWidth
              label="Meeting Link"
              placeholder="Enter meeting link (e.g., https://meet.google.com/xxx-xxxx-xxx)"
              value={meetingLinkInput}
              onChange={(e) => setMeetingLinkInput(e.target.value)}
              variant="outlined"
              multiline
              rows={2}
              sx={{ mt: 1 }}
            />
          </ArgonBox>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <ArgonButton
            onClick={handleCloseMeetingDialog}
            variant="outlined"
            color="secondary"
            size="small"
          >
            Cancel
          </ArgonButton>
          <ArgonButton
            onClick={handleUpdateMeetingLink}
            color="primary"
            size="small"
            variant="contained"
          >
            Update
          </ArgonButton>
        </DialogActions>
      </Dialog>
    </ArgonBox>
  );
}

export default summary;
